import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';

interface QueueItem {
  id: string;
  queueName: string;
  guestName: string;
  ticketNumber: string | null;
  orderNumber: string | null;
  location: string;
  status: string;
  position: number;
  createdAt: string;
}

interface QueueColumn {
  queueName: string;
  items: QueueItem[];
}

export function DeliveryQueueView() {
  const [queues, setQueues] = useState<QueueColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fetchQueues = useCallback(async () => {
    try {
      const data = await apiClient<QueueItem[]>('/packet-pickup/queue');
      // Group into a single queue column for display
      setQueues([{ queueName: 'Pending Pickups', items: data }]);
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Failed to load queues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueues();
    const interval = setInterval(fetchQueues, 10_000);
    return () => clearInterval(interval);
  }, [fetchQueues]);

  const handleRelease = async (itemId: string) => {
    setReleasing(itemId);
    try {
      await apiClient(`/packet-pickup/queue/${itemId}/release`, { method: 'PATCH' });
      // Remove from local state immediately
      setQueues((prev) =>
        prev.map((q) => ({
          ...q,
          items: q.items.filter((i) => i.id !== itemId),
        })),
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to release item');
    } finally {
      setReleasing(null);
    }
  };

  if (loading) {
    return <p style={styles.loadingText}>Loading delivery queues...</p>;
  }

  const totalPending = queues.reduce((sum, q) => sum + q.items.length, 0);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>📦 Delivery Queues</h3>
        <span style={styles.badge}>{totalPending} pending</span>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.columnsContainer}>
        {queues.map((queue) => (
          <div key={queue.queueName} style={styles.column}>
            <div style={styles.columnHeader}>
              <span style={styles.columnTitle}>{queue.queueName}</span>
              <span style={styles.columnCount}>{queue.items.length}</span>
            </div>
            <div style={styles.itemsList}>
              {queue.items.length === 0 && (
                <p style={styles.emptyText}>No items in queue</p>
              )}
              {queue.items.map((item, idx) => (
                <div key={item.id} style={styles.itemCard}>
                  <div style={styles.itemPosition}>#{idx + 1}</div>
                  <div style={styles.itemDetails}>
                    <div style={styles.itemName}>{item.guestName}</div>
                    <div style={styles.itemMeta}>
                      {item.ticketNumber && <span>{item.ticketNumber}</span>}
                      {item.orderNumber && <span style={{ marginLeft: '8px' }}>📋 {item.orderNumber}</span>}
                    </div>
                    <div style={styles.itemTime}>
                      {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' · '}
                      {item.location.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <button
                    style={{
                      ...styles.releaseBtn,
                      opacity: releasing === item.id ? 0.5 : 1,
                    }}
                    onClick={() => handleRelease(item.id)}
                    disabled={releasing === item.id}
                    aria-label={`Release ${item.guestName}`}
                  >
                    ✓
                  </button>
                  <button
                    style={{ ...styles.releaseBtn, borderColor: '#f87171', color: '#f87171', marginLeft: '6px', fontSize: '0.8rem' }}
                    onClick={async () => {
                      try {
                        await apiClient(`/packet-pickup/queue/${item.id}/release`, { method: 'PATCH' });
                        setQueues((prev) => prev.map((q) => ({ ...q, items: q.items.filter((i) => i.id !== item.id) })));
                      } catch { /* silent */ }
                    }}
                    aria-label={`Remove ${item.guestName} from queue`}
                    title="Undo / Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '12px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#c9a84c',
    margin: 0,
  },
  badge: {
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#c9a84c',
    color: '#0f1f17',
    padding: '3px 10px',
    borderRadius: '12px',
  },
  loadingText: {
    color: '#9ca3af',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '0.9rem',
  },
  errorBanner: {
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    fontSize: '0.82rem',
    marginBottom: '12px',
  },
  columnsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  column: {
    borderRadius: '10px',
    border: '1px solid #2a5a3e',
    backgroundColor: '#0f1f17',
    overflow: 'hidden',
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: '#1a3a2a',
    borderBottom: '1px solid #2a5a3e',
  },
  columnTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#fff',
  },
  columnCount: {
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#4ade80',
    color: '#0f2d1f',
    padding: '2px 8px',
    borderRadius: '10px',
    minWidth: '24px',
    textAlign: 'center',
  },
  itemsList: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '0.82rem',
    textAlign: 'center',
    padding: '16px 0',
    margin: 0,
  },
  itemCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: '#142920',
    border: '1px solid #2a5a3e',
  },
  itemPosition: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#6b7280',
    minWidth: '24px',
  },
  itemDetails: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemMeta: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginTop: '2px',
  },
  itemTime: {
    fontSize: '0.7rem',
    color: '#6b7280',
    marginTop: '2px',
  },
  releaseBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: '2px solid #4ade80',
    backgroundColor: 'transparent',
    color: '#4ade80',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background-color 0.15s',
  },
};
